import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatApiError, API_BASE } from '../../api';
import PageHeader from '../../components/PageHeader';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { ArrowLeft, Save, FileDown, Send, Link2, Plus, Trash2, Copy, Mail, Calendar as CalendarIcon, ExternalLink, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { PROPOSAL_STATUS, FREQUENCY_LABELS, GBP } from './constants';
import ProposalAnalytics from './ProposalAnalytics';
import { computeAddons, computeOption } from './compute';
import SendEmailDialog from '../../components/SendEmailDialog';
import NarrativeSection from './NarrativeSection';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import RichTextEditor from '../../components/RichTextEditor';

const FREQ_OPTIONS = ['monthly', 'quarterly', 'annually', 'annual_upfront'];

export default function ProposalDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [proposal, setProposal] = useState(null);
    const [modules, setModules] = useState([]);
    const [settings, setSettings] = useState(null);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [previewHostingId, setPreviewHostingId] = useState('_none');
    const [emailOpen, setEmailOpen] = useState(false);
    const [emailDefaults, setEmailDefaults] = useState({ to: '', subject: '' });
    const [emailSending, setEmailSending] = useState(false);

    const load = useCallback(async () => {
        try {
            const [p, m, s] = await Promise.all([
                api.get(`/proposals/${id}`),
                api.get('/modules'),
                api.get('/settings'),
            ]);
            setProposal(p.data);
            setModules(m.data.items.filter(x => x.active));
            setSettings(s.data);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || e.message);
            if (e?.response?.status === 404) navigate('/proposals');
        }
    }, [id, navigate]);

    useEffect(() => { load(); }, [load]);

    const update = (patch) => setProposal(p => ({ ...p, ...patch }));

    const vatRate = Number(settings?.default_vat_rate || 20);
    const adHocAddons = useMemo(
        () => computeAddons(proposal?.ad_hoc_modules || []),
        [proposal?.ad_hoc_modules]
    );
    const previewHosting = useMemo(() => {
        if (!previewHostingId || previewHostingId === '_none') return null;
        return (proposal?.hosting_options || []).find(h => h.id === previewHostingId) || null;
    }, [previewHostingId, proposal?.hosting_options]);
    const liveAddons = useMemo(() => {
        let extraSetup = adHocAddons.extra_setup;
        let extraMonthly = adHocAddons.extra_monthly;
        if (previewHosting && previewHosting.include_in_totals !== false) {
            extraSetup += Number(previewHosting.setup_fee || 0);
            extraMonthly += Number(previewHosting.monthly_support_fee || 0);
        }
        return {
            extra_setup: Math.round(extraSetup * 100) / 100,
            extra_monthly: Math.round(extraMonthly * 100) / 100,
        };
    }, [adHocAddons, previewHosting]);

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                title: proposal.title,
                proposal_date: proposal.proposal_date,
                valid_until: proposal.valid_until,
                scope_summary: proposal.scope_summary,
                selected_module_ids: proposal.selected_module_ids,
                ad_hoc_modules: (proposal.ad_hoc_modules || []).map(m => ({
                    id: m.id || undefined,
                    name: m.name,
                    description: m.description || null,
                    category: m.category || 'Bespoke',
                    suggested_setup_price: Number(m.suggested_setup_price) || 0,
                    suggested_monthly_price: Number(m.suggested_monthly_price) || 0,
                    internal_notes: m.internal_notes || null,
                    include_in_pricing: m.include_in_pricing !== false,
                    visible_on_proposal: m.visible_on_proposal !== false,
                })),
                hosting_options: (proposal.hosting_options || []).map(h => ({
                    id: h.id || undefined,
                    key: h.key || null,
                    label: h.label || '',
                    description: h.description || null,
                    example_url: h.example_url || null,
                    setup_fee: Number(h.setup_fee) || 0,
                    monthly_support_fee: Number(h.monthly_support_fee) || 0,
                    include_in_totals: h.include_in_totals !== false,
                    visible_on_proposal: h.visible_on_proposal !== false,
                    notes: h.notes || null,
                })),
                internal_notes: proposal.internal_notes,
                margin_notes: proposal.margin_notes,
                contract_terms_override: proposal.contract_terms_override ?? null,
                options: (proposal.options || []).map(o => ({
                    id: o.id,
                    name: o.name,
                    description: o.description || null,
                    setup_fee: Number(o.setup_fee) || 0,
                    setup_discount_pct: Number(o.setup_discount_pct) || 0,
                    deposit_pct: Number(o.deposit_pct) || 0,
                    monthly_saas_fee: Number(o.monthly_saas_fee) || 0,
                    annual_saas_fee_manual: o.annual_saas_fee_manual ? Number(o.annual_saas_fee_manual) : null,
                    contract_length_months: Number(o.contract_length_months) || 12,
                    minimum_commitment_months: o.minimum_commitment_months ? Number(o.minimum_commitment_months) : null,
                    payment_frequency: o.payment_frequency || 'monthly',
                    discount_pct: Number(o.discount_pct) || 0,
                    include_year_2_support: !!o.include_year_2_support,
                    year_2_support_fee: (o.year_2_support_fee !== '' && o.year_2_support_fee !== null && o.year_2_support_fee !== undefined)
                        ? Number(o.year_2_support_fee)
                        : null,
                    vat_applies: !!o.vat_applies,
                    recommended: !!o.recommended,
                    notes: o.notes || null,
                })),
            };
            const r = await api.patch(`/proposals/${id}`, body);
            setProposal(r.data);
            toast.success('Proposal saved');
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setSaving(false); }
    };

    const sendProposal = async () => {
        await save();
        setSending(true);
        try {
            const r = await api.patch(`/proposals/${id}`, { status: 'sent' });
            setProposal(r.data);
            toast.success('Proposal marked as sent');
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setSending(false); }
    };

    const copyPublicLink = () => {
        const origin = window.location.origin;
        const url = `${origin}/p/${proposal.public_token}`;
        navigator.clipboard.writeText(url).then(() => toast.success('Public link copied'));
    };

    const downloadPdf = () => {
        window.open(`${API_BASE}/proposals/${id}/pdf`, '_blank');
    };

    const openEmailDialog = async () => {
        try {
            const r = await api.get(`/clients/${proposal.client_id}/contacts`);
            const contacts = r.data.items || [];
            const primary = contacts.find(c => c.is_primary && c.email) || contacts.find(c => c.email);
            setEmailDefaults({
                to: primary?.email || '',
                subject: `Your proposal from Beyond Measure — ${proposal.title}`,
            });
        } catch {
            setEmailDefaults({ to: '', subject: `Your proposal from Beyond Measure — ${proposal.title}` });
        }
        setEmailOpen(true);
    };

    const sendEmail = async (to, subject, message, attachPdf) => {
        setEmailSending(true);
        const publicUrl = `${window.location.origin}/p/${proposal.public_token}`;
        try {
            await api.post(`/proposals/${id}/send-email`, {
                to, subject, message: message || null,
                attach_pdf: attachPdf,
                public_url: publicUrl,
            });
            toast.success(`Proposal emailed to ${to}`);
            setEmailOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to send'); }
        finally { setEmailSending(false); }
    };

    const addOption = () => update({
        options: [...(proposal.options || []), {
            name: `Option ${String.fromCharCode(65 + (proposal.options || []).length)} — Custom`,
            setup_fee: 0, deposit_pct: 0, monthly_saas_fee: 99,
            contract_length_months: 12, payment_frequency: 'monthly',
            discount_pct: 0, vat_applies: true,
        }],
    });

    const updateOption = (idx, patch) => {
        const opts = [...proposal.options];
        opts[idx] = { ...opts[idx], ...patch };
        update({ options: opts });
    };
    const removeOption = (idx) => update({ options: proposal.options.filter((_, i) => i !== idx) });

    if (!proposal) return <div className="p-8 text-[color:var(--bm-text-muted)]">Loading…</div>;

    const statusInfo = PROPOSAL_STATUS[proposal.status] || PROPOSAL_STATUS.draft;
    const canSend = ['draft'].includes(proposal.status);
    const publicLinkUrl = `${window.location.origin}/p/${proposal.public_token}`;
    const hasNarrative = !!proposal.narrative && Object.keys(proposal.narrative).length > 0;

    return (
        <div data-testid="proposal-detail-page" className="bm-fade-in">
            <PageHeader
                title={proposal.title}
                description={`${proposal.proposal_number} · ${proposal.client_name}`}
                actions={
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={() => navigate('/proposals')} className="h-9" data-testid="proposal-back-btn">
                            <ArrowLeft size={15} className="mr-1" /> Back
                        </Button>
                        <Button variant="outline" onClick={copyPublicLink} className="h-9" data-testid="proposal-copy-link-btn">
                            <Link2 size={15} className="mr-1" /> Copy link
                        </Button>
                        <Button variant="outline" onClick={downloadPdf} className="h-9" data-testid="proposal-pdf-btn">
                            <FileDown size={15} className="mr-1" /> PDF
                        </Button>
                        {!['rejected', 'expired', 'accepted'].includes(proposal.status) && (
                            <Button variant="outline" onClick={openEmailDialog} className="h-9" data-testid="proposal-email-btn">
                                <Mail size={15} className="mr-1" /> Send by email
                            </Button>
                        )}
                        <Button onClick={save} disabled={saving} className="h-9" style={{ background: 'var(--bm-primary)' }} data-testid="proposal-save-btn">
                            <Save size={15} className="mr-1" /> {saving ? 'Saving…' : 'Save'}
                        </Button>
                        {canSend && (
                            <Button onClick={sendProposal} disabled={sending} className="h-9" style={{ background: 'var(--bm-accent-600)' }} data-testid="proposal-send-btn">
                                <Send size={15} className="mr-1" /> {sending ? 'Sending…' : 'Mark sent'}
                            </Button>
                        )}
                    </div>
                }
            >
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <span className={`bm-pill ${statusInfo.tone} border`}>{statusInfo.label}</span>
                    {hasNarrative && (
                        <span className="bm-pill border bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-primary)] border-[color:var(--bm-primary)]/30" data-testid="proposal-narrative-active-pill">
                            <Sparkles size={11} className="mr-1 inline" /> Narrative active
                        </span>
                    )}
                    {proposal.public_view_count > 0 && (
                        <span className="text-[11.5px] text-[color:var(--bm-text-muted)]">Viewed {proposal.public_view_count}× · first {new Date(proposal.public_first_viewed_at).toLocaleString('en-GB')}</span>
                    )}
                </div>
            </PageHeader>

            {/* Client-facing link banner — the one URL to send to the customer. */}
            <div className="px-8 pt-4">
                <div className="bg-[color:var(--bm-bg-mute)] border border-[color:var(--bm-border)] rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="proposal-public-link-banner">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--bm-text-muted)]">Client link</span>
                    <a
                        href={publicLinkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 min-w-0 truncate font-mono text-[12.5px] text-[color:var(--bm-primary)] hover:underline"
                        data-testid="proposal-public-link-url"
                        title={publicLinkUrl}
                    >
                        {publicLinkUrl}
                    </a>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={copyPublicLink}
                        className="h-8"
                        data-testid="proposal-public-link-copy"
                    >
                        <Copy size={13} className="mr-1.5" /> Copy
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(publicLinkUrl, '_blank', 'noopener,noreferrer')}
                        className="h-8"
                        data-testid="proposal-public-link-open"
                    >
                        <ExternalLink size={13} className="mr-1.5" /> Open
                    </Button>
                </div>
            </div>

            <div className="px-8 py-6 space-y-6">
                {/* Analytics */}
                <ProposalAnalytics proposalId={proposal.id} />

                {/* Meta */}
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Title" value={proposal.title || ''} onChange={v => update({ title: v })} />
                        <Field label="Proposal date" type="date" value={proposal.proposal_date || ''} onChange={v => update({ proposal_date: v })} />
                        <Field label="Valid until" type="date" value={proposal.valid_until || ''} onChange={v => update({ valid_until: v })} />
                    </div>
                    <div className="mt-4">
                        <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Scope summary</Label>
                        <Textarea value={proposal.scope_summary || ''} onChange={e => update({ scope_summary: e.target.value })} rows={2} className="mt-1.5" />
                    </div>
                </div>

                {/* Narrative-proposal editor */}
                <NarrativeSection proposal={proposal} onUpdated={setProposal} />

                {/* Module selection */}
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6">
                    <div className="font-display text-[15px] font-semibold mb-4">Included modules</div>
                    <ModulePicker
                        selected={proposal.selected_module_ids || []}
                        onChange={(ids) => update({ selected_module_ids: ids })}
                        modules={modules}
                    />
                </div>

                {/* Ad-hoc / bespoke items */}
                <AdHocSection
                    items={proposal.ad_hoc_modules || []}
                    onChange={(items) => update({ ad_hoc_modules: items })}
                    onPromote={async (m) => {
                        try {
                            await api.post('/modules', {
                                name: m.name,
                                description: m.description || '',
                                category: m.category || 'Bespoke',
                                suggested_setup_price: Number(m.suggested_setup_price) || 0,
                                suggested_monthly_price: Number(m.suggested_monthly_price) || 0,
                                effort_rating: 3,
                                active: true,
                            });
                            toast.success(`"${m.name}" added to module library`);
                            const r = await api.get('/modules');
                            setModules(r.data.items.filter(x => x.active));
                        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
                    }}
                />

                {/* Hosting & Access — list of options offered to the client */}
                <HostingOptionsSection
                    items={proposal.hosting_options || []}
                    onChange={(items) => update({ hosting_options: items })}
                    clientName={proposal.client_name}
                    settings={settings}
                />

                {/* Commercial options */}
                <div>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                        <div className="text-[11.5px] uppercase tracking-[0.1em] text-[color:var(--bm-text-subtle)] font-semibold">Commercial options</div>
                        <div className="flex items-center gap-3">
                            {(proposal.hosting_options || []).length > 0 && (
                                <div className="flex items-center gap-2" data-testid="admin-hosting-preview">
                                    <Label className="text-[11.5px] text-[color:var(--bm-text-muted)] whitespace-nowrap">Preview with hosting:</Label>
                                    <Select value={previewHostingId} onValueChange={setPreviewHostingId}>
                                        <SelectTrigger className="h-8 w-[220px] text-[12.5px]" data-testid="admin-hosting-preview-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="_none">Base only (no hosting)</SelectItem>
                                            {(proposal.hosting_options || []).map(h => (
                                                <SelectItem key={h.id} value={h.id}>
                                                    {h.label}{h.include_in_totals !== false && (h.setup_fee > 0 || h.monthly_support_fee > 0)
                                                        ? ` (+${GBP(h.setup_fee)} / ${GBP(h.monthly_support_fee)}mo)` : ''}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            <Button onClick={addOption} variant="outline" size="sm" className="h-8" data-testid="proposal-add-option-btn">
                                <Plus size={14} className="mr-1" /> Add option
                            </Button>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                        {(proposal.options || []).map((opt, idx) => (
                            <OptionCard
                                key={opt.id || idx}
                                option={opt}
                                onChange={(patch) => updateOption(idx, patch)}
                                onRemove={() => removeOption(idx)}
                                index={idx}
                                addons={liveAddons}
                                vatRate={vatRate}
                                proposalDate={proposal.proposal_date}
                                previewHosting={previewHosting}
                                adHocAddons={adHocAddons}
                            />
                        ))}
                    </div>
                </div>

                {/* Contract terms (editable per-proposal; defaults to settings.default_contract_terms) */}
                <ContractTermsSection
                    proposal={proposal}
                    settings={settings}
                    onChange={(v) => update({ contract_terms_override: v })}
                />

                {/* Internal notes */}
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6">
                    <div className="font-display text-[15px] font-semibold mb-4">Internal notes (not shown to client)</div>
                    <Textarea value={proposal.internal_notes || ''} onChange={e => update({ internal_notes: e.target.value })} rows={3} className="mb-3" placeholder="Private working notes…" />
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Margin / effort notes</Label>
                    <Textarea value={proposal.margin_notes || ''} onChange={e => update({ margin_notes: e.target.value })} rows={2} className="mt-1.5" />
                </div>
            </div>

            <SendEmailDialog
                open={emailOpen}
                onClose={() => setEmailOpen(false)}
                defaultTo={emailDefaults.to}
                defaultSubject={emailDefaults.subject}
                showAttachPdf
                defaultAttachPdf={false}
                sending={emailSending}
                onSend={sendEmail}
                bodyPreview={
                    <>
                        <div className="flex justify-between"><span>{proposal.proposal_number}</span><span className="font-semibold">{proposal.title}</span></div>
                        <div className="text-[color:var(--bm-primary)]">+ View Proposal button (public link)</div>
                        {proposal.valid_until && <div className="flex justify-between"><span>Valid until</span><span>{proposal.valid_until}</span></div>}
                    </>
                }
            />
        </div>
    );
}

function Field({ label, value, onChange, type = 'text' }) {
    return (
        <div>
            <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">{label}</Label>
            <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 h-10" />
        </div>
    );
}

function ModulePicker({ selected, onChange, modules }) {
    const selSet = new Set(selected);
    const grouped = useMemo(() => modules.reduce((acc, m) => {
        const cat = m.category || 'Other';
        (acc[cat] = acc[cat] || []).push(m);
        return acc;
    }, {}), [modules]);
    const toggle = (id) => {
        const s = new Set(selSet);
        s.has(id) ? s.delete(id) : s.add(id);
        onChange(Array.from(s));
    };
    return (
        <div className="space-y-4" data-testid="module-picker">
            {Object.keys(grouped).sort().map(cat => (
                <div key={cat}>
                    <div className="text-[10.5px] uppercase tracking-[0.12em] text-[color:var(--bm-text-subtle)] font-semibold mb-2">{cat}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {grouped[cat].map(m => (
                            <label key={m.id} className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition ${selSet.has(m.id) ? 'border-[color:var(--bm-accent)] bg-[color:var(--bm-accent-50)]' : 'border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)]'}`}
                                   data-testid={`module-pick-${m.id}`}>
                                <Checkbox checked={selSet.has(m.id)} onCheckedChange={() => toggle(m.id)} className="mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[12.5px] font-medium text-[color:var(--bm-text)]">{m.name}</div>
                                    <div className="text-[11px] text-[color:var(--bm-text-muted)] line-clamp-1">{m.description}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function OptionCard({ option, onChange, onRemove, index, addons, vatRate, proposalDate, previewHosting, adHocAddons }) {
    const c = useMemo(
        () => computeOption(option, vatRate, addons, proposalDate),
        [option, vatRate, addons, proposalDate]
    );
    const isAnnualUpfront = (option.payment_frequency || 'monthly') === 'annual_upfront';

    // Build breakdown rows for monthly + setup when addons > 0
    const monthlyParts = [];
    const setupParts = [];
    const baseSetup = Number(option.setup_fee || 0);
    const baseMonthly = Number(option.monthly_saas_fee || 0);
    if (adHocAddons && adHocAddons.extra_setup > 0) setupParts.push(['Ad-hoc items', adHocAddons.extra_setup]);
    if (adHocAddons && adHocAddons.extra_monthly > 0) monthlyParts.push(['Ad-hoc items', adHocAddons.extra_monthly]);
    if (previewHosting && previewHosting.include_in_totals !== false) {
        const hs = Number(previewHosting.setup_fee || 0);
        const hm = Number(previewHosting.monthly_support_fee || 0);
        if (hs > 0) setupParts.push([`Hosting — ${previewHosting.label}`, hs]);
        if (hm > 0) monthlyParts.push([`Hosting — ${previewHosting.label}`, hm]);
    }

    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden" data-testid={`option-card-${index}`}>
            <div className="px-5 py-4 border-b border-[color:var(--bm-border)] bg-[color:var(--bm-bg-mute)] flex items-start justify-between gap-3">
                <Input value={option.name || ''} onChange={e => onChange({ name: e.target.value })} className="h-9 text-[13px] font-semibold bg-white" data-testid={`option-name-${index}`} />
                <button onClick={onRemove} className="text-[color:var(--bm-text-subtle)] hover:text-red-600 p-1 shrink-0"><Trash2 size={15} /></button>
            </div>
            <div className="p-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                    <NumField label="Setup fee (£)"      value={option.setup_fee}        onChange={v => onChange({ setup_fee: v })} testId={`option-${index}-setup`} />
                    <NumField label="Deposit on setup (%)" value={option.deposit_pct}     onChange={v => onChange({ deposit_pct: v })} />
                    <div>
                        <NumField
                            label="Setup fee discount (%)"
                            value={option.setup_discount_pct}
                            onChange={v => onChange({ setup_discount_pct: v })}
                            testId={`option-${index}-setup-discount`}
                        />
                        <p className="text-[10.5px] text-[color:var(--bm-text-muted)] mt-1 leading-tight">
                            One-off % off the setup fee. Shown as a separate line on the client proposal.
                        </p>
                    </div>
                    <NumField label="Monthly SaaS (£)"   value={option.monthly_saas_fee} onChange={v => onChange({ monthly_saas_fee: v })} testId={`option-${index}-monthly`} />
                    <NumField label="Annual override (£)" value={option.annual_saas_fee_manual ?? ''} onChange={v => onChange({ annual_saas_fee_manual: v === '' ? null : v })} />
                    <NumField label="Contract length (mo)" value={option.contract_length_months} onChange={v => onChange({ contract_length_months: v })} />
                    <NumField label="Min commitment (mo)"  value={option.minimum_commitment_months ?? ''} onChange={v => onChange({ minimum_commitment_months: v === '' ? null : v })} />
                    <div>
                        <Label className="text-[11.5px] text-[color:var(--bm-text-muted)]">Payment frequency</Label>
                        <Select value={option.payment_frequency || 'monthly'} onValueChange={v => onChange({ payment_frequency: v })}>
                            <SelectTrigger className="mt-1.5 h-9" data-testid={`option-${index}-frequency`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {FREQ_OPTIONS.map(f => <SelectItem key={f} value={f}>{FREQUENCY_LABELS[f]}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <NumField
                            label="Annual upfront discount (%)"
                            value={option.discount_pct}
                            onChange={v => onChange({ discount_pct: v })}
                            testId={`option-${index}-discount`}
                        />
                        <p className="text-[10.5px] text-[color:var(--bm-text-muted)] mt-1 leading-tight">
                            Shown on the client's proposal as savings when they choose "Pay upfront annual".
                        </p>
                    </div>
                </div>

                {/* Year 2 support fee — optional, surfaced separately */}
                <div className="pt-2 border-t border-dashed border-[color:var(--bm-border)] space-y-2">
                    <label className="flex items-center gap-2 text-[12px] text-[color:var(--bm-text)] font-medium">
                        <Checkbox
                            checked={!!option.include_year_2_support}
                            onCheckedChange={(v) => onChange({ include_year_2_support: !!v })}
                            data-testid={`option-${index}-y2-toggle`}
                        />
                        Include a Year 2 support fee
                        <span className="text-[10.5px] text-[color:var(--bm-text-muted)] font-normal">
                            (Year 1 = implementation support · Year 2 onwards = ongoing support only)
                        </span>
                    </label>
                    {option.include_year_2_support && (
                        <div className="grid grid-cols-2 gap-3 pl-6">
                            <NumField
                                label="Year 2 support fee (£/mo)"
                                value={option.year_2_support_fee ?? ''}
                                onChange={v => onChange({ year_2_support_fee: v === '' ? null : v })}
                                testId={`option-${index}-y2-fee`}
                            />
                            <div className="text-[11px] text-[color:var(--bm-text-muted)] self-center">
                                Leave blank to match Year 1 monthly support fee ({GBP(baseMonthly)}/mo).
                            </div>
                        </div>
                    )}
                </div>
                <label className="flex items-center gap-2 text-[12px] text-[color:var(--bm-text-muted)] pt-1">
                    <Checkbox checked={!!option.vat_applies} onCheckedChange={(v) => onChange({ vat_applies: !!v })} />
                    Apply VAT
                </label>
                <label className="flex items-center gap-2 text-[12px] text-[color:var(--bm-text)] font-medium">
                    <Checkbox
                        checked={!!option.recommended}
                        onCheckedChange={(v) => onChange({ recommended: !!v })}
                        data-testid={`option-${index}-recommended`}
                    />
                    Mark as recommended option <span className="text-[10.5px] text-[color:var(--bm-text-muted)] font-normal">(shows highlighted pill to client)</span>
                </label>

                {/* Live-computed summary with clear labels + breakdown */}
                <div className="mt-3 pt-3 border-t border-dashed border-[color:var(--bm-border)] space-y-2 text-[12px]" data-testid={`option-${index}-computed`}>
                    {previewHosting && (
                        <div className="-mt-1 text-[10.5px] uppercase tracking-wide text-[color:var(--bm-accent-600)] font-semibold">
                            Previewing with: {previewHosting.label}
                        </div>
                    )}

                    {/* Monthly block */}
                    <div>
                        {monthlyParts.length > 0 && (
                            <>
                                <SummaryRow label="Base monthly SaaS" value={GBP(baseMonthly)} muted />
                                {monthlyParts.map(([lbl, val], i) => (
                                    <SummaryRow key={`m${i}`} label={lbl} value={`+ ${GBP(val)}`} muted />
                                ))}
                            </>
                        )}
                        <SummaryRow label="Monthly total" value={GBP(c.monthly_total)} strong />
                    </div>

                    {/* Annual recurring block */}
                    <div>
                        {c.discount_amount > 0 && (
                            <>
                                <SummaryRow label="Annual recurring before discount" value={GBP(c.annual_total_gross)} muted />
                                <SummaryRow label="Discount recurring support" value={`− ${GBP(c.discount_amount)}`} accent />
                            </>
                        )}
                        <SummaryRow label="Annual recurring total" value={GBP(c.annual_total_net)} strong />
                        {c.include_year_2_support && c.year_2_annual > 0 && (
                            <>
                                <SummaryRow label="Year 2 support (monthly)" value={`${GBP(c.year_2_monthly)} / mo`} muted />
                                <SummaryRow label="Year 2 support (annual)" value={GBP(c.year_2_annual)} />
                            </>
                        )}
                    </div>

                    {/* Setup block */}
                    <div>
                        {setupParts.length > 0 && (
                            <>
                                <SummaryRow label="Base setup fee" value={GBP(baseSetup)} muted />
                                {setupParts.map(([lbl, val], i) => (
                                    <SummaryRow key={`s${i}`} label={lbl} value={`+ ${GBP(val)}`} muted />
                                ))}
                            </>
                        )}
                        {c.setup_discount_amount > 0 && (
                            <>
                                <SummaryRow label="Setup before discount" value={GBP(c.setup_total_gross)} muted />
                                <SummaryRow label={`Setup fee discount (${c.setup_discount_pct}%)`} value={`− ${GBP(c.setup_discount_amount)}`} accent />
                            </>
                        )}
                        <SummaryRow label="Setup total" value={GBP(c.setup_total)} strong />
                        {c.deposit_due > 0 && <SummaryRow label="Deposit due" value={GBP(c.deposit_due)} muted />}
                    </div>

                    {/* Year-one block */}
                    <div className="pt-2 border-t border-[color:var(--bm-border)]">
                        <SummaryRow label="Year-one total excluding VAT" value={GBP(c.year_one_total_net)} strong />
                        {c.vat_amount > 0 && <SummaryRow label="VAT" value={GBP(c.vat_amount)} muted />}
                        <SummaryRow label="Year-one total including VAT" value={GBP(c.total_inc_vat)} strong accent />
                        {isAnnualUpfront && (
                            <div className="mt-1 text-[10.5px] text-[color:var(--bm-text-subtle)] italic">
                                Annual upfront: Year-one total = Setup total + Annual recurring total
                            </div>
                        )}
                        <SummaryRow label="Minimum commitment value" value={GBP(c.minimum_commitment_value)} muted />
                        {c.renewal_date_example && <SummaryRow label="Example renewal" value={c.renewal_date_example} muted />}
                    </div>
                </div>
            </div>
        </div>
    );
}

function NumField({ label, value, onChange, testId }) {
    return (
        <div>
            <Label className="text-[11.5px] text-[color:var(--bm-text-muted)]">{label}</Label>
            <Input type="number" step="0.01" value={value ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} className="mt-1.5 h-9" data-testid={testId} />
        </div>
    );
}

function SummaryRow({ label, value, strong, muted, accent }) {
    return (
        <div className="flex items-center justify-between">
            <span className={`${muted ? 'text-[color:var(--bm-text-subtle)]' : 'text-[color:var(--bm-text-muted)]'}`}>{label}</span>
            <span className={`${strong ? 'font-semibold' : ''} ${accent ? 'text-[color:var(--bm-accent-600)]' : 'text-[color:var(--bm-text)]'}`}>{value}</span>
        </div>
    );
}

/* ── Ad-hoc / bespoke items ─────────────────────────────────────────────── */
function AdHocSection({ items, onChange, onPromote }) {
    const [settings, setSettings] = useState(null);
    useEffect(() => { api.get('/settings').then(r => setSettings(r.data)).catch(() => {}); }, []);

    const add = () => onChange([...(items || []), {
        name: '', description: '', category: 'Bespoke',
        suggested_setup_price: 0, suggested_monthly_price: 0,
        internal_notes: '', include_in_pricing: true, visible_on_proposal: true,
    }]);
    const patch = (idx, p) => {
        const next = [...items]; next[idx] = { ...next[idx], ...p }; onChange(next);
    };
    const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6" data-testid="ad-hoc-section">
            <div className="flex items-center justify-between mb-2">
                <div>
                    <div className="font-display text-[15px] font-semibold">Additional items (bespoke)</div>
                    <div className="text-[11.5px] text-[color:var(--bm-text-subtle)] mt-0.5">One-off proposal items that don't live in the module library. Optionally save to the library later.</div>
                </div>
                <Button variant="outline" size="sm" onClick={add} data-testid="ad-hoc-add-btn"><Plus size={14} className="mr-1" /> Add item</Button>
            </div>
            {(!items || items.length === 0) && (
                <div className="text-[12.5px] text-[color:var(--bm-text-muted)] py-4 text-center border border-dashed border-[color:var(--bm-border)] rounded-lg mt-3">
                    No additional items. Examples: Custom dashboard · Bespoke import tool · Training session · Domain setup support.
                </div>
            )}
            <div className="space-y-3 mt-3">
                {(items || []).map((m, idx) => (
                    <div key={idx} className="border border-[color:var(--bm-border)] rounded-lg p-4" data-testid={`ad-hoc-item-${idx}`}>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                            <div className="md:col-span-4">
                                <Label className="text-[11.5px] text-[color:var(--bm-text-muted)]">Name *</Label>
                                <Input value={m.name || ''} onChange={e => patch(idx, { name: e.target.value })} className="mt-1.5 h-9" data-testid={`ad-hoc-name-${idx}`} />
                            </div>
                            <div className="md:col-span-3">
                                <Label className="text-[11.5px] text-[color:var(--bm-text-muted)]">Category</Label>
                                <Input value={m.category || 'Bespoke'} onChange={e => patch(idx, { category: e.target.value })} className="mt-1.5 h-9" />
                            </div>
                            <div className="md:col-span-2">
                                <Label className="text-[11.5px] text-[color:var(--bm-text-muted)]">Setup (£)</Label>
                                <Input type="number" step="0.01" value={m.suggested_setup_price ?? 0}
                                       onChange={e => patch(idx, { suggested_setup_price: Number(e.target.value) })}
                                       className="mt-1.5 h-9" data-testid={`ad-hoc-setup-${idx}`} />
                            </div>
                            <div className="md:col-span-2">
                                <Label className="text-[11.5px] text-[color:var(--bm-text-muted)]">Monthly (£)</Label>
                                <Input type="number" step="0.01" value={m.suggested_monthly_price ?? 0}
                                       onChange={e => patch(idx, { suggested_monthly_price: Number(e.target.value) })}
                                       className="mt-1.5 h-9" data-testid={`ad-hoc-monthly-${idx}`} />
                            </div>
                            <div className="md:col-span-1 flex items-end justify-end">
                                <button onClick={() => remove(idx)} className="text-[color:var(--bm-text-subtle)] hover:text-red-600 p-1 mb-1" data-testid={`ad-hoc-remove-${idx}`}><Trash2 size={15} /></button>
                            </div>
                        </div>
                        <div className="mt-3">
                            <Label className="text-[11.5px] text-[color:var(--bm-text-muted)]">Description (shown on proposal)</Label>
                            <Textarea value={m.description || ''} onChange={e => patch(idx, { description: e.target.value })} rows={2} className="mt-1.5" />
                        </div>
                        <div className="mt-3">
                            <Label className="text-[11.5px] text-[color:var(--bm-text-muted)]">Internal notes (never shown on proposal)</Label>
                            <Textarea value={m.internal_notes || ''} onChange={e => patch(idx, { internal_notes: e.target.value })} rows={2} className="mt-1.5" placeholder="Private context, margin notes, caveats…" />
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-5">
                            <label className="flex items-center gap-2 text-[12.5px] text-[color:var(--bm-text)]">
                                <Checkbox checked={m.include_in_pricing !== false} onCheckedChange={v => patch(idx, { include_in_pricing: !!v })} data-testid={`ad-hoc-include-${idx}`} />
                                Include in pricing
                            </label>
                            <label className="flex items-center gap-2 text-[12.5px] text-[color:var(--bm-text)]">
                                <Checkbox checked={m.visible_on_proposal !== false} onCheckedChange={v => patch(idx, { visible_on_proposal: !!v })} data-testid={`ad-hoc-visible-${idx}`} />
                                Show on proposal
                            </label>
                            {m.name && (
                                <Button type="button" variant="outline" size="sm" onClick={() => onPromote(m)} className="ml-auto h-8" data-testid={`ad-hoc-promote-${idx}`}>
                                    Save to module library
                                </Button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── Hosting options (list) ─────────────────────────────────────────────── */
function HostingOptionsSection({ items, onChange, clientName, settings }) {
    const suffix = settings?.hosting_subdomain_suffix || 'beyondmeasure.global';
    const defaultSetup = Number(settings?.default_domain_setup_fee ?? 250);
    const defaultMonthly = Number(settings?.default_domain_monthly_support_fee ?? 15);
    const slug = (clientName || 'client').toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 30) || 'client';

    const defaults = () => ([
        { key: 'bm_subdomain', label: 'Beyond Measure subdomain',
          description: 'Hosted on a secure Beyond Measure subdomain. No client DNS setup required.',
          example_url: `${slug}.${suffix}`,
          setup_fee: 0, monthly_support_fee: 0,
          include_in_totals: true, visible_on_proposal: true, notes: '' },
        { key: 'client_domain', label: 'Client-owned domain / subdomain',
          description: 'Fully branded experience on your domain (e.g. portal.clientname.co.uk). Includes DNS configuration, SSL checks and setup support.',
          example_url: `portal.${slug}.co.uk`,
          setup_fee: defaultSetup, monthly_support_fee: defaultMonthly,
          include_in_totals: true, visible_on_proposal: true, notes: '' },
        { key: 'tbc', label: 'To be confirmed',
          description: 'Finalise hosting choice after acceptance.',
          example_url: '',
          setup_fee: 0, monthly_support_fee: 0,
          include_in_totals: false, visible_on_proposal: true, notes: '' },
    ]);

    const addDefaults = () => onChange(defaults());
    const addBlank = () => onChange([...(items || []), {
        key: 'custom', label: '', description: '', example_url: '',
        setup_fee: 0, monthly_support_fee: 0,
        include_in_totals: true, visible_on_proposal: true, notes: '',
    }]);
    const patch = (idx, p) => {
        const next = [...items]; next[idx] = { ...next[idx], ...p }; onChange(next);
    };
    const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6" data-testid="hosting-options-section">
            <div className="flex items-center justify-between">
                <div>
                    <div className="font-display text-[15px] font-semibold">Hosting &amp; Access — options to offer</div>
                    <div className="text-[11.5px] text-[color:var(--bm-text-subtle)] mt-0.5">The client will pick one on the public proposal. Totals update dynamically based on their choice. Defaults editable in Settings.</div>
                </div>
                <div className="flex gap-2">
                    {(!items || items.length === 0) && (
                        <Button size="sm" variant="outline" onClick={addDefaults} data-testid="hosting-add-defaults-btn">Seed 3 defaults</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={addBlank} data-testid="hosting-add-blank-btn"><Plus size={14} className="mr-1" /> Add option</Button>
                </div>
            </div>

            {(!items || items.length === 0) && (
                <div className="text-[12.5px] text-[color:var(--bm-text-muted)] py-4 text-center border border-dashed border-[color:var(--bm-border)] rounded-lg mt-3">
                    No hosting options yet. Click <span className="font-medium">Seed 3 defaults</span> to start with BM subdomain, Client domain, and TBC.
                </div>
            )}

            <div className="space-y-3 mt-3">
                {(items || []).map((h, idx) => (
                    <div key={h.id || idx} className="border border-[color:var(--bm-border)] rounded-lg p-4" data-testid={`hosting-option-${idx}`}>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                            <div className="md:col-span-5">
                                <Label className="text-[11.5px] text-[color:var(--bm-text-muted)]">Label *</Label>
                                <Input value={h.label || ''} onChange={e => patch(idx, { label: e.target.value })} className="mt-1.5 h-9" data-testid={`hosting-option-${idx}-label`} />
                            </div>
                            <div className="md:col-span-4">
                                <Label className="text-[11.5px] text-[color:var(--bm-text-muted)]">Example URL</Label>
                                <Input value={h.example_url || ''} onChange={e => patch(idx, { example_url: e.target.value })} className="mt-1.5 h-9" placeholder="portal.clientname.co.uk" data-testid={`hosting-option-${idx}-example`} />
                            </div>
                            <div className="md:col-span-2">
                                <Label className="text-[11.5px] text-[color:var(--bm-text-muted)]">Setup (£)</Label>
                                <Input type="number" step="0.01" value={h.setup_fee ?? 0} onChange={e => patch(idx, { setup_fee: Number(e.target.value) })} className="mt-1.5 h-9" data-testid={`hosting-option-${idx}-setup`} />
                            </div>
                            <div className="md:col-span-1 flex items-end justify-end">
                                <button onClick={() => remove(idx)} className="text-[color:var(--bm-text-subtle)] hover:text-red-600 p-1 mb-1" data-testid={`hosting-option-${idx}-remove`}><Trash2 size={15} /></button>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mt-3">
                            <div className="md:col-span-8">
                                <Label className="text-[11.5px] text-[color:var(--bm-text-muted)]">Description (shown to client)</Label>
                                <Textarea value={h.description || ''} onChange={e => patch(idx, { description: e.target.value })} rows={2} className="mt-1.5" />
                            </div>
                            <div className="md:col-span-4">
                                <Label className="text-[11.5px] text-[color:var(--bm-text-muted)]">Monthly support (£/mo)</Label>
                                <Input type="number" step="0.01" value={h.monthly_support_fee ?? 0} onChange={e => patch(idx, { monthly_support_fee: Number(e.target.value) })} className="mt-1.5 h-9" data-testid={`hosting-option-${idx}-monthly`} />
                            </div>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-5">
                            <label className="flex items-center gap-2 text-[12.5px] text-[color:var(--bm-text)]">
                                <Checkbox checked={h.include_in_totals !== false} onCheckedChange={v => patch(idx, { include_in_totals: !!v })} data-testid={`hosting-option-${idx}-include`} />
                                Include in totals when selected
                            </label>
                            <label className="flex items-center gap-2 text-[12.5px] text-[color:var(--bm-text)]">
                                <Checkbox checked={h.visible_on_proposal !== false} onCheckedChange={v => patch(idx, { visible_on_proposal: !!v })} data-testid={`hosting-option-${idx}-visible`} />
                                Show on public proposal
                            </label>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function OptionRadio({ active, onClick, title, subtitle, badge, testId }) {
    return (
        <button type="button" onClick={onClick} data-testid={testId}
                className={`text-left border rounded-lg p-3 transition ${active ? 'border-[color:var(--bm-accent)] bg-[color:var(--bm-accent-50)]' : 'border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)]'}`}>
            <div className="flex items-center justify-between gap-2">
                <div className="text-[12.5px] font-semibold text-[color:var(--bm-text)]">{title}</div>
                {badge && <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-white border border-[color:var(--bm-border)] text-[color:var(--bm-text-muted)]">{badge}</span>}
            </div>
            <div className="text-[11.5px] text-[color:var(--bm-text-muted)] mt-1 truncate">{subtitle}</div>
        </button>
    );
}

/**
 * Editable contract-terms block. The proposal can override the global
 * `settings.default_contract_terms`. When the client accepts the proposal,
 * the contract snapshots the override (if set) — otherwise the settings
 * default — into `agreement_terms_snapshot`, which the signed-agreement PDF
 * renders verbatim.
 */
function ContractTermsSection({ proposal, settings, onChange }) {
    const defaultTerms = settings?.default_contract_terms || '';
    const override = proposal.contract_terms_override;
    const isUsingDefault = override == null;
    const currentValue = isUsingDefault ? defaultTerms : override;

    // Debounce-style commit: TipTap fires onChange on every keystroke. We
    // forward edits to the parent immediately (state) but only persist when
    // the value diverges from the default — so the "Using default" pill stays
    // accurate and we don't store identical override copies.
    const commit = (next) => {
        const trimmedNext = (next || '').trim();
        const trimmedDefault = (defaultTerms || '').trim();
        if (!trimmedNext || trimmedNext === trimmedDefault) {
            if (override != null) onChange(null);
            return;
        }
        if (trimmedNext !== (override || '').trim()) onChange(next);
    };

    const resetToDefault = () => {
        if (override != null) onChange(null);
        toast.success('Reverted to default contract terms');
    };

    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6" data-testid="contract-terms-section">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div>
                    <div className="font-display text-[15px] font-semibold">Contract terms</div>
                    <p className="text-[12px] text-[color:var(--bm-text-muted)] mt-0.5">
                        These terms appear on the public T&amp;Cs page (where clients can read &amp; download before signing) and on the signed-agreement PDF. Edit below to override the company default for this proposal only.
                    </p>
                </div>
                <span
                    className={`bm-pill border text-[11px] ${isUsingDefault ? 'bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]' : 'bg-[color:var(--bm-accent-50)] text-[color:var(--bm-primary)] border-[color:var(--bm-primary)]/30'}`}
                    data-testid="contract-terms-status"
                >
                    {isUsingDefault ? 'Using company default' : 'Custom for this proposal'}
                </span>
            </div>

            <RichTextEditor
                value={currentValue || ''}
                onChange={commit}
                minHeight={320}
                placeholder={defaultTerms ? 'Edit to override the company default for this proposal.' : 'No default contract terms set in Settings. Add them here to include on the signed agreement.'}
                testId="contract-terms-editor"
            />

            <div className="mt-3 flex items-center justify-between gap-2 flex-wrap text-[11.5px] text-[color:var(--bm-text-muted)]">
                <span>
                    Formatting (bold, underline, headings, lists) is preserved on the public T&amp;Cs page and signed PDF.
                </span>
                {!isUsingDefault && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={resetToDefault}
                        className="h-7 text-[11px]"
                        data-testid="contract-terms-reset"
                    >
                        Reset to company default
                    </Button>
                )}
            </div>
        </div>
    );
}

